import {
  createUserGoal,
  getActiveGoals,
  getArchiveGoals,
  getUserGoal,
  deleteUserGoal,
  completeGoal,
  updateGoalDifficulty,
  toggleArchiveGoal,
  toggleAchievementGoal,
  renameUserGoal,
  updateGoalCategory,
} from '../api/userGoalApi';
import { getGoalChecks, getGoalDoneToday, toggleGoalDoneToday } from '../api/userGoalCheckApi';
import UserGlobalYearCalendar from '../usercomponents/UserGlobalYearCalendar';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { addEntry, deleteEntry, renameEntry } from '../api/userEntryApi';
import { getProgress, addXP, removeXP } from '../api/userRankApi';
import { getGlobalContributions } from '../api/userGoalCheckApi';
import UserAddGoalForm from '../usercomponents/UserAddGoalForm';
import UserRankPanel from '../components/rank/UserRankPanel';
import GoalCelebration from '../components/GoalCelebration';
import UserGoalCard from '../usercomponents/UserGoalCard';
import { reorderUserGoals } from '../api/userGoalApi';
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { getPublicProfile } from '../api/userApi';
import api from '../api/userApi';
import '../styles.css';
import SocialModal from '../components/SocialModal';
import { useUserActions } from '../hooks/useUserActions';
import { useGoalActions } from '../hooks/useGoalActions';
import { getLastYearRange } from '../utils/dateUtils';
import GoalPrivacyManager from '../components/GoalPrivacyManager';
import ProfilePicUpload from '../components/ProfilePicUpload';

function Home({ currentUser }) {
  const { userId } = useParams();

  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [openGoals, setOpenGoals] = useState({});
  const [goalDetails, setGoalDetails] = useState({});
  const [goalCheckDates, setGoalCheckDates] = useState({});
  const [isLoadingGoalDetails, setIsLoadingGoalDetails] = useState(false);
  const [globalContributions, setGlobalContributions] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isLoadingGoals, setIsLoadingGoals] = useState(false);
  const [doneTodayByGoal, setDoneTodayByGoal] = useState({});
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [viewedMonths, setViewedMonths] = useState({});
  const [entryInputs, setEntryInputs] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [progress, setProgress] = useState(null);
  const [userList, setUserList] = useState([]);
  const [achievements, setAchievements] = useState([]);

  const isOwnProfile = currentUser && String(currentUser.id) === String(userId);
  const loadingGoalLock = useRef({});

  const [processingGoals, setProcessingGoals] = useState({});

  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const { handleUpdateBio, handleTogglePrivacyForAll } = useUserActions({
    setProfile,
    setMyAchievements: setAchievements, // Assuming this updates your achievements state
  });

  const {
    handleRenameGoal,
    handleDeleteGoal,
    handleUpdateCategory,
    handleToggleArchive,
    handleViewGoal,
    handleChangeEntryInput,
    handleAddEntry,
    handleDeleteEntry,
    handleRenameEntry,
    loadGoalDetails,
    handleMonthNav,
    handleOpenList,
  } = useGoalActions({
    setGoals,
    setGoalDetails,
    setGoalCheckDates,
    setOpenGoals,
    setViewedMonths,
    setEntryInputs,
    entryInputs,
    loadingGoalLock,
    setShowModal,
    setModalTitle,
    setUserList,
  });

  const handleFollowStatusChange = (changedUserId, isNowFollowing) => {
    setProfile((prev) => {
      if (!prev) return prev;
      const newProfile = { ...prev };

      // CASE 1: You followed the person whose profile you are actually visiting
      // (This updates their "Followers" count)
      if (String(changedUserId) === String(userId)) {
        newProfile.isFollowing = isNowFollowing;
        newProfile.followersCount = isNowFollowing
          ? prev.followersCount + 1
          : Math.max(0, prev.followersCount - 1);
      }

      // CASE 2: You followed someone while looking at YOUR OWN profile
      newProfile.followingCount = isNowFollowing
        ? prev.followingCount + 1
        : Math.max(0, prev.followingCount - 1);

      return newProfile;
    });
  };

  // Load user goals
  useEffect(() => {
    if (!currentUser) return;

    const fetchGoals = async () => {
      try {
        setLoading(true);

        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const [profileData, activeRes, progressRes] = await Promise.all([
          getPublicProfile(currentUser.id, tz),
          getActiveGoals(),
          getProgress(),
        ]);

        setProfile(profileData);

        const loadedGoals = activeRes.data || [];
        setGoals(loadedGoals);

        const doneStatusMapping = {};
        loadedGoals.forEach((goal) => {
          doneStatusMapping[goal.id] = goal.doneToday;
        });
        setDoneTodayByGoal(doneStatusMapping);

        setProgress(progressRes.data);

        // Let the main page show now
        setLoading(false);

        // Load heavier stuff after
        loadGlobalContributions();

        // Optional: do NOT auto-load opened goal details on first page load
        localStorage.removeItem('homeOpenGoals');
        setOpenGoals({});
      } catch (err) {
        console.error('Failed to load goals', err);
        setLoading(false);
      }
    };

    fetchGoals();
  }, [currentUser]);

  const loadGlobalContributions = async () => {
    try {
      const range = getLastYearRange();
      const res = await getGlobalContributions(range.from, range.to);
      setGlobalContributions(res.data);
    } catch (err) {
      console.error('Error loading contributions:', err);
    }
  };

  // Drag-and-drop
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const newOrder = Array.from(goals);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);

    setGoals(newOrder);

    try {
      const payload = newOrder.map((goal, index) => ({
        id: goal.id, // Make sure this matches your backend's expected ID
        position: index,
      }));
      await reorderUserGoals(payload);
    } catch (err) {
      console.error('Failed to persist goal order', err);
    }
  };

  const loadGoals = async () => {
    try {
      setIsLoadingGoals(true);

      const res = await getActiveGoals();
      const newGoals = res.data || [];

      setGoals(newGoals);

      const doneStatusMapping = {};
      newGoals.forEach((goal) => {
        doneStatusMapping[goal.id] = goal.doneToday;
      });

      setDoneTodayByGoal(doneStatusMapping);
    } catch (err) {
      console.error('Error loading goals:', err);
    } finally {
      setIsLoadingGoals(false);
    }
  };

  // Inside Home.jsx
  const handleAddGoal = async (goalData) => {
    try {
      const { title, difficulty, dailyHabit, reminderEnabled, reminderTime, reminderDays } =
        goalData;

      await createUserGoal(
        title,
        difficulty,
        dailyHabit,
        reminderEnabled,
        reminderTime,
        reminderDays,
      );

      await loadGoals();
      setIsFormVisible(false);
    } catch (err) {
      console.error('Error creating goal:', err);
    }
  };

  const handleMarkDoneToday = async (goalId, difficulty) => {
    console.log('🔔 handleMarkDoneToday called for goal:', goalId); // ✅ add this
    if (processingGoals[goalId]) return; // ✅ Block if already processing

    setProcessingGoals((prev) => ({ ...prev, [goalId]: true }));

    try {
      const res = await toggleGoalDoneToday(goalId);
      const doneToday = res.data.doneToday;

      const wasDone = doneTodayByGoal[goalId] ?? false;

      // Update button state
      setDoneTodayByGoal((prev) => {
        const updated = { ...prev, [goalId]: doneToday };
        console.log('UPDATED doneTodayByGoal:', updated);
        return updated;
      });

      const pad2 = (num) => (num < 10 ? '0' + num : String(num));

      // Optimistic calendar update
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;

      setGoalCheckDates((prev) => {
        const existing = Array.isArray(prev[goalId]) ? prev[goalId] : [];

        if (doneToday && !existing.includes(todayStr)) {
          return { ...prev, [goalId]: [...existing, todayStr] };
        }

        if (!doneToday) {
          return { ...prev, [goalId]: existing.filter((d) => d !== todayStr) };
        }

        return prev;
      });

      // Backend XP update and live progress refresh
      if ((doneToday && !wasDone) || (!doneToday && wasDone)) {
        if (doneToday) await addXP(difficulty);
        else await removeXP(difficulty);
        setShowCelebration(true);
        const progressRes = await getProgress();
        setProgress(progressRes.data);
      }

      // Update global calendar
      await loadGlobalContributions();
    } catch (err) {
      console.error('Error toggle done today:', err);
    } finally {
      setProcessingGoals((prev) => ({ ...prev, [goalId]: false })); // ✅ Unlock
    }
  };

  const handleCompleteGoal = async (goalId, difficulty) => {
    const confirmComplete = window.confirm('Mark this goal as completed?');
    if (!confirmComplete) return;

    try {
      await completeGoal(goalId); // move to achievements
      await loadGoals();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDiffcultyChange = async (goalId, newDifficulty) => {
    try {
      console.log('sending difficulty:', newDifficulty, typeof newDifficulty);
      await updateGoalDifficulty(goalId, newDifficulty);
      setGoals((prev) =>
        prev.map((g) => (g.id === goalId ? { ...g, difficulty: newDifficulty } : g)),
      );
    } catch (err) {
      console.error('Failed to update difficulty', err);
    }
  };

  const handleToggleAchievement = async (goalId) => {
    try {
      await toggleAchievementGoal(goalId);

      await loadGoals();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <p>Loading your goals...</p>;

  return (
    <div className="app-container" data-testid="home-page">
      {showPrivacyModal && profile && (
        <GoalPrivacyManager
          profile={profile}
          goals={goals}
          achievements={achievements}
          onUpdateBio={handleUpdateBio}
          onTogglePrivacy={handleTogglePrivacyForAll}
          onClose={() => setShowPrivacyModal(false)}
        />
      )}
      <header className="profile-view-header home-profile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* ✅ Profile pic on the left */}
          <ProfilePicUpload
            currentPicUrl={profile?.profilePicUrl}
            onUploadSuccess={(url) => setProfile((prev) => ({ ...prev, profilePicUrl: url }))}
          />

          <div className="profile-info-stack">
            <h1 className="profile-name-text">
              {currentUser.firstName} {currentUser.lastName}
            </h1>
            {profile?.bio && <p className="profile-bio-display">{profile.bio}</p>}
            <div className="stats-mini">
              <span
                onClick={() => handleOpenList(currentUser.id, 'followers')}
                className="clickable-stat"
              >
                <strong>{profile.followersCount}</strong> Followers
              </span>
              <span
                onClick={() => handleOpenList(currentUser.id, 'following')}
                className="following-stat"
              >
                <strong>{profile.followingCount}</strong> Following
              </span>
            </div>
          </div>
        </div>

        <div className="profile-header-actions">
          <Link
            to="/achievements"
            className="achievement-link-btn"
            title="View Achievement Vault"
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#fbbf24';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span style={{ fontSize: '1.4rem' }}>🏆</span>
          </Link>
        </div>
      </header>
      {isFormVisible && (
        <UserAddGoalForm onAdd={handleAddGoal} onClose={() => setIsFormVisible(false)} />
      )}

      <UserRankPanel progress={progress} />

      <UserGlobalYearCalendar contributions={globalContributions} />

      <GoalCelebration
        isVisible={showCelebration}
        onClose={() => setShowCelebration(false)}
        // We pass the actual numbers now:
        completedCount={Object.values(doneTodayByGoal).filter(Boolean).length}
        totalCount={goals.length}
      />

      {isLoadingGoals && <p>Loading goals...</p>}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          className="btn-primary"
          data-testid="open-add-goal-form-button"
          onClick={() => setIsFormVisible(!isFormVisible)}
        >
          + Add Goal
        </button>
      </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="goals">
          {(provided) => (
            <ul
              className="goal-list"
              data-testid="goal-list"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {goals.map((goal, index) => {
                const goalKey = goal.id;
                const doneToday = doneTodayByGoal[goal.id] === true;
                if (!goalKey) return null;

                const isOpen = openGoals[goalKey] === true;

                return (
                  <Draggable key={goalKey} draggableId={String(goalKey)} index={index}>
                    {(provided) => (
                      <li ref={provided.innerRef} {...provided.draggableProps}>
                        <UserGoalCard
                          goal={goal}
                          selectedGoal={goalDetails[goalKey]}
                          isOpen={isOpen}
                          onView={() => handleViewGoal(goal.id, 'homeOpenGoals')}
                          dragHandleProps={provided.dragHandleProps}
                          checkDates={goalCheckDates[goalKey] || []}
                          viewedMonth={viewedMonths[goalKey]}
                          onPrevMonth={() => handleMonthNav(goal.id, 'PREV', loadGoalDetails)}
                          onNextMonth={() => handleMonthNav(goal.id, 'NEXT', loadGoalDetails)}
                          onDelete={() => handleDeleteGoal(goal.id, loadGoals)}
                          onMarkDoneToday={handleMarkDoneToday}
                          newEntryDescription={entryInputs[goal.id] || ''}
                          onChangeNewEntry={(text) => handleChangeEntryInput(goal.id, text)}
                          onAddEntry={() => handleAddEntry(goal.id)}
                          onRenameEntry={(entryId, text) =>
                            handleRenameEntry(goal.id, entryId, text)
                          }
                          onComplete={handleCompleteGoal}
                          doneToday={doneToday}
                          onDifficultyChange={handleDiffcultyChange}
                          isArchived={false}
                          isAchievementPage={false}
                          onToggleArchive={handleToggleArchive}
                          onToggleAchievement={() => handleToggleAchievement(goal.id)}
                          handleToggleAchievement={handleToggleAchievement}
                          onRename={(id, title) => handleRenameGoal(id, title, loadGoals)}
                          onDeleteEntry={(entryId) => handleDeleteEntry(goal.id, entryId)}
                          isReadOnly={false}
                          onUpdateCategory={handleUpdateCategory}
                          isProcessing={processingGoals[goal.id] || false}
                        />
                      </li>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>

      {goals.length === 0 && <p>No goals yet.</p>}
      {isLoadingGoalDetails && <p>Loading goal details...</p>}
      <SocialModal
        isOpen={showModal}
        title={modalTitle}
        userList={userList}
        setUserList={setUserList}
        onClose={() => setShowModal(false)}
        currentUserId={currentUser?.id}
        onFollowStatusChange={handleFollowStatusChange}
        isOwnFollowersList={modalTitle === 'Followers'}
      />
    </div>
  );
}

export default Home;
